import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatStepper } from '@angular/material/stepper';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { EstablishmentService } from '../../establishment.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { ContractModel, ContractAnalysisResult } from '../../../../../../core/models/contract.model';
import { ContractService } from '../../../contract/contract.service';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';

export interface EstablishmentMainEstablishmentUpdateData {
  establishment: EstablishmentModel;
}

@Component({
  selector: 'vex-establishment-main-establishment-update',
  templateUrl: './establishment-main-establishment-update.component.html',
  styleUrls: ['./establishment-main-establishment-update.component.scss'],
})
export class EstablishmentMainEstablishmentUpdateComponent implements OnInit {
  form: UntypedFormGroup;
  loading = false;
  // Données pour l'autocomplete des établissements
  establishmentItems$: Observable<EstablishmentModel[]>;
  // Indicateur pour savoir si l'utilisateur a choisi de passer en mode indépendant
  showIndependentFields = false;
  // États des steps
  step1Completed = false;
  step2Completed = false;
  step3Completed = false;
  // Mode de sélection : 'linked' ou 'independent'
  selectionMode: 'linked' | 'independent' = 'linked';
  // Case de confirmation pour le step 3
  confirmationChecked = false;
  // Propriétés pour l'analyse d'impact des contrats
  currentEstablishmentContracts: ContractModel[] = [];
  compatibleContracts: ContractAnalysisResult[] = [];
  problematicContracts: ContractAnalysisResult[] = [];
  public readonly crudModeEnum = CrudModeEnum;

  analysisLoading = false;
  @ViewChild('stepper', { static: false }) stepper!: MatStepper;

  constructor(
    private fb: UntypedFormBuilder,
    private establishmentService: EstablishmentService,
    private snackbarService: SnackbarService,
    private contractService: ContractService,
    private dialogRef: MatDialogRef<EstablishmentMainEstablishmentUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EstablishmentMainEstablishmentUpdateData,
  ) {
    this.initForm();
  }

  get currentMainEstablishment(): EstablishmentModel | null {
    return this.data.establishment.mainEstablishmentData || null;
  }

  get hasMainEstablishment(): boolean {
    return !!this.data.establishment.mainEstablishmentId;
  }

  get newMainEstablishment(): EstablishmentModel | null {
    return this.form.get('mainEstablishment')?.value || null;
  }

  get headerLabel(): string {
    return this.hasMainEstablishment
      ? "Modification de l'établissement principal"
      : "Ajout de l'établissement principal";
  }

  ngOnInit() {
    this.establishmentAutocompleteChange();

    // Initialiser le mode selon l'état actuel
    // En mode ajout (pas de mainEstablishment), forcer le mode 'linked'
    // En mode modification (mainEstablishment existe), partir sur le mode 'linked' par défaut
    this.selectionMode = 'linked';
    this.showIndependentFields = false;
  }

  establishmentAutocompleteChange(state = '') {
    // Utilisation de findAllAsSelect comme dans les autres composants
    this.establishmentItems$ = this.establishmentService
      .findAllAsSelect(
        false, // pas de noLimit pour éviter de charger tous les établissements
        'label',
        'asc',
        state,
        {
          establishmentType: 'CARE', // Seuls les établissements de type CARE peuvent être des établissements principaux
        },
      )
      .pipe(map(x => x.data));
  }

  onMainEstablishmentChange(value: EstablishmentModel | null) {
    // Si on supprime l'établissement principal (value === null), on affiche les champs indépendants
    this.showIndependentFields = !value;

    if (this.showIndependentFields) {
      // Ajouter les validations required pour les champs indépendants
      this.form.get('sirenNumber')?.setValidators([Validators.required]);
      this.form.get('siretNumber')?.setValidators([Validators.required]);
      this.form.get('tva')?.setValidators([Validators.required]);
    } else {
      // Supprimer les validations si on a sélectionné un établissement
      this.form.get('sirenNumber')?.clearValidators();
      this.form.get('siretNumber')?.clearValidators();
      this.form.get('tva')?.clearValidators();

      // Vider les champs
      this.form.patchValue({
        sirenNumber: '',
        siretNumber: '',
        tva: '',
      });
    }

    // Mettre à jour les validations
    this.form.get('sirenNumber')?.updateValueAndValidity();
    this.form.get('siretNumber')?.updateValueAndValidity();
    this.form.get('tva')?.updateValueAndValidity();
  }

  onModeChange(mode: 'linked' | 'independent') {
    this.selectionMode = mode;
    this.showIndependentFields = mode === 'independent';

    if (mode === 'independent') {
      this.form.patchValue({
        mainEstablishment: null,
      });

      this.form.get('isFrenchCompany')?.enable();
      this.form.get('tva')?.setValidators([Validators.required]);
      this.form.get('tva')?.updateValueAndValidity();

      const isFrenchCompany = this.form.get('isFrenchCompany')?.value;
      this.toggleCompanyFields(isFrenchCompany);
    } else {
      this.establishmentAutocompleteChange();

      this.form.patchValue({
        sirenNumber: '',
        siretNumber: '',
        dunsNumber: '',
        tva: '',
        isFrenchCompany: true,
      });

      this.form.get('isFrenchCompany')?.disable();
      this.form.get('sirenNumber')?.clearValidators();
      this.form.get('siretNumber')?.clearValidators();
      this.form.get('dunsNumber')?.clearValidators();
      this.form.get('tva')?.clearValidators();
      this.form.get('sirenNumber')?.updateValueAndValidity();
      this.form.get('siretNumber')?.updateValueAndValidity();
      this.form.get('dunsNumber')?.updateValueAndValidity();
      this.form.get('tva')?.updateValueAndValidity();
    }
  }

  close() {
    this.dialogRef.close();
  }

  nextStep() {
    if (this.canProceedToStep2()) {
      this.step1Completed = true;
      this.stepper.next();
      // Déclencher l'analyse d'impact des contrats
      this.analyzeContractsImpact();
    }
  }

  previousStep() {
    this.stepper.previous();
  }

  canProceedToStep2(): boolean {
    if (this.selectionMode === 'linked') {
      const mainEstValue = this.form.get('mainEstablishment')?.value;
      const result = !!mainEstValue;
      return result;
    } else {
      const isFrenchCompany = this.form.get('isFrenchCompany')?.value;

      const tvaControl = this.form.get('tva');
      const tvaValid = tvaControl?.valid && !!tvaControl?.value;

      if (isFrenchCompany) {
        const sirenControl = this.form.get('sirenNumber');
        const siretControl = this.form.get('siretNumber');

        // SIREN est auto-complété et désactivé, donc on vérifie juste qu'il a une valeur
        // Un champ disabled a toujours valid=false même sans erreur
        const sirenValid = !!sirenControl?.value && sirenControl?.value.toString().length === 9;
        const siretValid = siretControl?.valid && !!siretControl?.value;

        const result = !!(sirenValid && siretValid && tvaValid);
        return result;
      } else {
        const dunsControl = this.form.get('dunsNumber');
        const siretControl = this.form.get('siretNumber');
        const sirenControl = this.form.get('sirenNumber');

        const dunsValid = dunsControl?.valid && !!dunsControl?.value;

        const result = !!(dunsValid && tvaValid);
        return result;
      }
    }
  }

  analyzeContractsImpact() {
    this.analysisLoading = true;

    // Initialiser les tableaux vides par défaut
    this.compatibleContracts = [];
    this.problematicContracts = [];
    this.currentEstablishmentContracts = [];

    try {
      const selectedEstablishment = this.form.get('mainEstablishment')?.value;
      const newMainEstablishmentId = this.selectionMode === 'linked' ? selectedEstablishment?._id : null;

      if (!this.data.establishment?._id) {
        throw new Error("ID de l'établissement manquant");
      }

      this.establishmentService
        .analyzeEstablishmentContractsCompatibility(this.data.establishment._id, newMainEstablishmentId)
        .subscribe({
          next: response => {
            if (response && response.data) {
              this.compatibleContracts = response.data.compatible || [];
              this.problematicContracts = response.data.incompatible || [];

              // Pour la compatibilité avec l'affichage existant
              this.currentEstablishmentContracts = [
                ...this.compatibleContracts.map(c => c?.contract).filter(c => c),
                ...this.problematicContracts.map(c => c?.contract).filter(c => c),
              ];
            }

            this.step2Completed = true;
            this.analysisLoading = false;
          },
          error: error => {
            console.error("Erreur lors de l'analyse des contrats:", error);
            this.snackbarService.danger("Erreur lors de l'analyse des contrats");
            this.compatibleContracts = [];
            this.problematicContracts = [];
            this.currentEstablishmentContracts = [];
            this.analysisLoading = false;
          },
        });
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'analyse:", error);
      this.snackbarService.danger("Erreur lors de l'initialisation de l'analyse");
      this.analysisLoading = false;
    }
  }

  toggleCompanyFields(isFrenchCompany: boolean) {
    const siretControl = this.form.get('siretNumber');
    const sirenControl = this.form.get('sirenNumber');
    const dunsControl = this.form.get('dunsNumber');

    if (isFrenchCompany) {
      dunsControl?.setValue('');
      dunsControl?.clearValidators();
      dunsControl?.markAsUntouched();
      dunsControl?.markAsPristine();
      dunsControl?.updateValueAndValidity();

      siretControl?.setValidators([Validators.required, Validators.pattern(/^[0-9]{14}$/)]);
      sirenControl?.setValidators([Validators.required, Validators.pattern(/^[0-9]{9}$/)]);
      siretControl?.updateValueAndValidity();
      sirenControl?.updateValueAndValidity();
    } else {
      siretControl?.setValue(null);
      sirenControl?.setValue(null);
      siretControl?.clearValidators();
      sirenControl?.clearValidators();
      siretControl?.markAsUntouched();
      siretControl?.markAsPristine();
      sirenControl?.markAsUntouched();
      sirenControl?.markAsPristine();
      siretControl?.updateValueAndValidity();
      sirenControl?.updateValueAndValidity();

      dunsControl?.setValidators([Validators.required]);
      dunsControl?.updateValueAndValidity();
    }
  }

  async onConfirmChanges() {
    if (this.form.valid) {
      this.loading = true;

      try {
        const selectedEstablishment = this.form.get('mainEstablishment')?.value;
        const newMainEstablishmentId = selectedEstablishment?._id || null;

        const updateData: any = {
          mainEstablishmentId: newMainEstablishmentId,
        };

        if (this.showIndependentFields && !newMainEstablishmentId) {
          const isFrenchCompany = this.form.get('isFrenchCompany')?.value;

          if (isFrenchCompany) {
            updateData.sirenNumber = this.form.get('sirenNumber')?.value;
            updateData.siretNumber = this.form.get('siretNumber')?.value;
            updateData.dunsNumber = null;
          } else {
            updateData.dunsNumber = this.form.get('dunsNumber')?.value;
            updateData.sirenNumber = null;
            updateData.siretNumber = null;
          }

          updateData.tva = this.form.get('tva')?.value;
        }

        await this.establishmentService
          .updateMainEstablishment(this.data.establishment._id, newMainEstablishmentId, updateData)
          .toPromise();

        this.snackbarService.success(
          newMainEstablishmentId
            ? "L'établissement principal a été modifié avec succès"
            : "L'établissement principal a été supprimé avec succès. L'établissement est maintenant indépendant.",
        );

        this.dialogRef.close(true);
      } catch (error) {
        console.error("Erreur lors de la modification de l'établissement principal:", error);
        const fieldError = (error as any)?.error?.errors?.[0]?.message;
        this.snackbarService.danger(fieldError || 'Une erreur est survenue lors de la modification');
      } finally {
        this.loading = false;
      }
    }
  }

  openContractInNewTab(contractId: string) {
    if (!contractId) {
      return;
    }
    const contractUrl = `/management/engagement/${contractId}/perimeter`;
    const fullUrl = window.location.origin + contractUrl;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }

  getTotalImpactedProducts(): number {
    return this.problematicContracts.reduce((total, contract) => {
      return total + (contract.incompatibleDetails?.reduce((sum, detail) => sum + (detail.productCount || 0), 0) || 0);
    }, 0);
  }

  getTotalProductsForContract(contract: ContractAnalysisResult): number {
    return contract.incompatibleDetails?.reduce((sum, detail) => sum + (detail.productCount || 0), 0) || 0;
  }

  nextToConfirmation() {
    if (this.step2Completed) {
      this.stepper.next();
    }
  }

  canConfirm(): boolean {
    if (this.problematicContracts.length === 0) {
      return true;
    }
    return this.confirmationChecked;
  }

  private initForm() {
    this.form = this.fb.group({
      mainEstablishment: [this.data.establishment.mainEstablishmentData || null],
      sirenNumber: ['', []],
      siretNumber: ['', []],
      dunsNumber: ['', []],
      tva: ['', []],
      isFrenchCompany: [true],
    });

    this.form.get('mainEstablishment')?.valueChanges.subscribe(value => {
      this.onMainEstablishmentChange(value);
    });

    this.form.get('isFrenchCompany')?.valueChanges.subscribe((isFrench: boolean) => {
      this.toggleCompanyFields(isFrench);
    });

    this.form.get('siretNumber')?.valueChanges.subscribe((siret: string | null) => {
      const sirenControl = this.form.get('sirenNumber');
      if (siret && this.selectionMode === 'independent') {
        const siretStr = siret.toString();
        const siren = siretStr.substring(0, 9);
        sirenControl?.setValue(siren);
      } else if (!siret && this.selectionMode === 'independent') {
        sirenControl?.reset();
      }
    });
  }
}
